if (!customElements.get('selling-plan-widget')) {
  class SellingPlansWidget extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;

      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onPurchaseTypeChange = this.onPurchaseTypeChange.bind(this);
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.purchaseData = this.parsePurchaseData();

      this.attachEventListeners();
      this.syncCurrentState();
    }

    disconnectedCallback() {
      this.abortController?.abort();

      this.quantityInputs.forEach((input) => {
        input.removeEventListener('change', this.onQuantityChange);
      });

      this.purchaseTypeInputs.forEach((input) => {
        input.removeEventListener('change', this.onPurchaseTypeChange);
      });
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get shopifySection() {
      return document.querySelector(`#shopify-section-${this.sectionId}`);
    }

    get sectionRoot() {
      return this.shopifySection || this.closest('[id^="shopify-section-"]') || this;
    }

    get productForm() {
      return this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId)
        : this.closest('form[action*="/cart/add"]');
    }

    get sellingPlanInput() {
      return this.sectionRoot.querySelector('.selected-selling-plan-id');
    }

    get purchaseTypePropertyInput() {
      return this.querySelector('.selected-purchase-type-property');
    }

    get quantityInputs() {
      return [...this.sectionRoot.querySelectorAll('input[name="quantity-picker"]')];
    }

    get purchaseTypeInputs() {
      return [...this.querySelectorAll('.selling-plan__option-input')];
    }

    get selectedQuantityInput() {
      return this.quantityInputs.find((input) => input.checked) || this.quantityInputs[0] || null;
    }

    get selectedPurchaseTypeInput() {
      return this.purchaseTypeInputs.find((input) => input.checked) || this.purchaseTypeInputs[0] || null;
    }

    get selectedPurchaseType() {
      return this.selectedPurchaseTypeInput?.value || 'subscription';
    }

    get currentVariantData() {
      if (!this.purchaseData?.variants || !this.selectedQuantityInput) return null;

      return this.purchaseData.variants[this.selectedQuantityInput.value] || null;
    }

    parsePurchaseData() {
      const dataElement = this.querySelector('[data-purchase-options-data]');
      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        return console.error('Failed to parse purchase options data', error);
      }
    }

    attachEventListeners() {
      const signal = this.abortController?.signal;

      this.quantityInputs.forEach((input) => {
        input.addEventListener('change', this.onQuantityChange, { signal });
      });

      this.purchaseTypeInputs.forEach((input) => {
        input.addEventListener('change', this.onPurchaseTypeChange, { signal });
      });
    }

    onQuantityChange(event) {
      if (!event.target.checked) return;
      this.syncCurrentState();
    }

    onPurchaseTypeChange(event) {
      if (!event.target.checked) return;
      this.syncCurrentState();
    }

    syncCurrentState() {
      const variantData = this.currentVariantData;
      if (!variantData) return;

      this.updateOptionState();
      this.updateSellingPlanInput(variantData);
      this.updateQuantityCards();
      this.updatePurchaseOptionCards(variantData);
      this.renderList('[data-benefits-list]', 'selling-plan__benefit', variantData.benefits || []);
      this.renderList('[data-one-time-benefits]', 'selling-plan__benefit', variantData.oneTimeBenefits || []);
      this.renderList('[data-drawbacks-list]', 'selling-plan__drawback', variantData.oneTimeDrawbacks || []);
      this.updateGiftInputs(variantData.gifts || []);
    }

    updateOptionState() {
      this.querySelectorAll('.selling-plan__option').forEach((option) => {
        const optionType = option.dataset.purchaseOption === 'one-time' ? 'one_time' : 'subscription';
        option.classList.toggle('is-active', optionType === this.selectedPurchaseType);
      });
    }

    updateSellingPlanInput(variantData) {
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = this.selectedPurchaseType === 'subscription'
          ? variantData.sellingPlanId || ''
          : '';
      }

      if (this.purchaseTypePropertyInput) {
        this.purchaseTypePropertyInput.value = this.selectedPurchaseType === 'subscription'
          ? 'Subscription'
          : 'One-time';
      }
    }

    updateQuantityCards() {
      const isSubscription = this.selectedPurchaseType === 'subscription';

      this.quantityInputs.forEach((input) => {
        const variantData = this.purchaseData?.variants?.[input.value];
        if (!variantData) return;

        const card = this.sectionRoot.querySelector(`label[for="${input.id}"]`);
        if (!card) return;

        const priceElement = card.querySelector('[data-quantity-price]');
        const compareElement = card.querySelector('[data-quantity-compare]');
        const saveElement = card.querySelector('[data-quantity-save]');
        const saveBadge = saveElement?.closest('.quantity-picker__badge');

        const price = isSubscription ? variantData.subscriptionPrice : variantData.oneTimePrice;
        const isSale = isSubscription ? variantData.isSaleSubscription : variantData.isSaleOneTime;
        const savePercent = isSubscription ? variantData.subscriptionSavePercent : variantData.oneTimeSavePercent;

        if (priceElement) {
          priceElement.textContent = price;
          priceElement.classList.toggle('is-sale', Boolean(isSale));
        }

        if (compareElement) {
          compareElement.textContent = variantData.comparePrice;
          compareElement.hidden = !isSale;
        }

        if (saveElement) {
          saveElement.textContent = savePercent || '';
        }

        if (saveBadge) {
          saveBadge.hidden = !isSale;
        }
      });
    }

    updatePurchaseOptionCards(variantData) {
      const subscriptionCompare = this.querySelector('[data-subscription-compare]');
      const subscriptionPrice = this.querySelector('[data-subscription-price]');
      const subscriptionUnit = this.querySelector('[data-subscription-unit]');
      const subscriptionItems = this.querySelector('[data-subscription-items]');
      const subscriptionSave = this.querySelector('[data-subscription-save]');
      const subscriptionSaveBadge = subscriptionSave?.closest('.selling-plan__option-save-badge');
      const oneTimeCompare = this.querySelector('[data-one-time-compare]');
      const oneTimePrice = this.querySelector('[data-one-time-price]');
      const oneTimeUnit = this.querySelector('[data-one-time-unit]');
      const oneTimeItems = this.querySelector('[data-one-time-items]');
      const oneTimeSave = this.querySelector('[data-one-time-save]');
      const oneTimeSaveBadge = oneTimeSave?.closest('.selling-plan__option-save-badge');

      if (subscriptionCompare) {
        subscriptionCompare.textContent = variantData.comparePrice;
        subscriptionCompare.hidden = !variantData.isSaleSubscription;
      }

      if (subscriptionPrice) {
        subscriptionPrice.textContent = variantData.subscriptionPrice;
        subscriptionPrice.classList.toggle('is-sale', Boolean(variantData.isSaleSubscription));
      }

      if (subscriptionUnit) {
        subscriptionUnit.textContent = variantData.subscriptionUnitPrice;
      }

      if (subscriptionItems) {
        subscriptionItems.textContent = variantData.itemsPerBox;
      }

      if (subscriptionSave) {
        subscriptionSave.textContent = variantData.subscriptionSavePercent || '';
      }

      if (subscriptionSaveBadge) {
        subscriptionSaveBadge.hidden = !variantData.isSaleSubscription;
      }

      if (oneTimeCompare) {
        oneTimeCompare.textContent = variantData.comparePrice;
        oneTimeCompare.hidden = !variantData.isSaleOneTime;
      }

      if (oneTimePrice) {
        oneTimePrice.textContent = variantData.oneTimePrice;
        oneTimePrice.classList.toggle('is-sale', Boolean(variantData.isSaleOneTime));
      }

      if (oneTimeUnit) {
        oneTimeUnit.textContent = variantData.oneTimeUnitPrice;
      }

      if (oneTimeItems) {
        oneTimeItems.textContent = variantData.itemsPerBox;
      }

      if (oneTimeSave) {
        oneTimeSave.textContent = variantData.oneTimeSavePercent || '';
      }

      if (oneTimeSaveBadge) {
        oneTimeSaveBadge.hidden = !variantData.isSaleOneTime;
      }
    }

    renderBenefits(benefits) {
      this.renderList('[data-benefits-list]', 'selling-plan__benefit', benefits);
    }

    renderDrawbacks(drawbacks) {
      this.renderList('[data-drawbacks-list]', 'selling-plan__drawback', drawbacks);
    }

    renderList(listSelector, itemClassName, items) {
      const list = this.querySelector(listSelector);
      if (!list) return;

      list.replaceChildren();

      items.forEach((text) => {
        const item = document.createElement('li');
        item.className = itemClassName;

        item.textContent = text;
        list.appendChild(item);
      });
    }

    updateGiftInputs(gifts) {
      const giftInputs = [...this.sectionRoot.querySelectorAll('[data-subscription-gift-input]')];
      if (giftInputs.length === 0) return;

      const mainVariantId = this.productForm?.querySelector('[name="id"]')?.value || '';
      const isSubscription = this.selectedPurchaseType === 'subscription';
      const giftVariantIds = isSubscription
        ? (gifts || [])
          .filter((gift) => gift.available)
          .flatMap((gift) => gift.variantIds || [])
          .filter(Boolean)
        : [];

      giftInputs.forEach((input) => {
        const index = Number.parseInt(input.dataset.giftIndex || '', 10);
        const field = input.dataset.giftField;
        const variantId = Number.isNaN(index) ? '' : (giftVariantIds[index] || '');
        const isEnabled = Boolean(isSubscription && variantId && mainVariantId);

        if (field === 'id') {
          input.value = isEnabled ? variantId : '';
        }

        if (field === 'quantity') {
          input.value = isEnabled ? '1' : '';
        }

        if (field === 'parent_id') {
          input.value = isEnabled ? mainVariantId : '';
        }

        input.disabled = !isEnabled;
      });
    }
  }

  customElements.define('selling-plan-widget', SellingPlansWidget);
}
